import axiosInstance from '../utils/axios';
import userService from './userService';

const reportService = {










  /**
   * 백엔드 API를 통한 PDF 보고서 생성 및 다운로드
   * 권한 검증 및 감사 로그가 포함된 안전한 방식
   * @param {Array} reports - 보고서 데이터 (사용자명 추출용)
   * @param {number} userIdx - 사용자 ID
   * @param {number} period - 조회 기간
   * @returns {Promise<void>}
   */
  async downloadPdfReport(reports, userIdx, period) {
    try {
      // 백엔드 API를 통한 PDF 생성 요청 (권한 검증 및 감사 로그 포함)
      const response = await axiosInstance.post('/reports/generate', {
        user_idx: userIdx,
        period: period
      });

      if (response.data.success) {
        // 안전한 데이터 추출
        const responseData = response.data.data || {};
        const download_url = responseData.download_url || responseData['download_url'];
        const file_name = responseData.file_name || responseData['file_name'];
        
        // download_url과 file_name이 유효한지 확인
        if (!download_url || !file_name) {
          throw new Error('서버에서 다운로드 URL 또는 파일명을 받지 못했습니다.');
        }
        
        // URL이 상대경로인지 확인하고 절대경로로 변환
        const fullDownloadUrl = download_url.startsWith('/') ? download_url : `/${download_url}`;
        
        const downloadResponse = await axiosInstance.get(fullDownloadUrl, {
          responseType: 'blob',
          timeout: 30000, // 30초 타임아웃
          headers: {
            'Accept': 'application/pdf'
          }
        });
        
        // Blob 객체가 아닌 경우 처리
        if (!(downloadResponse.data instanceof Blob)) {
          throw new Error('서버에서 올바른 파일 형식을 받지 못했습니다.');
        }
        
        // 작은 파일 크기인 경우 내용을 확인 (에러 응답일 가능성)
        if (downloadResponse.data.size < 1000) {
          const text = await downloadResponse.data.text();
          throw new Error(`PDF 다운로드 실패: ${text}`);
        }
        
        // 사용자명과 기간 정보로 파일명 생성
        const userName = await this.extractUserName(userIdx);
        const dateRange = this.formatDateRange(reports);
        const customFileName = `${userName} ${dateRange} 보고서.pdf`;
        
        const blob = new Blob([downloadResponse.data], { type: 'application/pdf' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = customFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
      } else {
        throw new Error(response.data.message || 'PDF 생성 실패');
      }
    } catch (error) {
      throw error;
    }
  },

  /**
   * userService를 통해 사용자명 추출
   * @param {number} userIdx - 사용자 ID
   * @returns {Promise<string>} 사용자명
   */
  async extractUserName(userIdx) {
    try {
      const result = await userService.getUserName(userIdx);
      if (result.success && result.data?.userName) {
        return `${result.data.userName}님`;
      }
    } catch (error) {
      console.error('사용자명 조회 실패:', error);
    }
    return '사용자';
  },

  /**
   * 보고서 데이터에서 실제 날짜 범위 추출
   * @param {Array} reports - 보고서 데이터
   * @returns {string} 날짜 범위 문자열
   */
  formatDateRange(reports) {
    if (!reports || reports.length === 0) {
      return '날짜미상';
    }
    
    const formatDate = (dateString) => {
      if (!dateString) return null;
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return null;
      
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}.${month}.${day}`;
    };
    
    // 첫 번째 레코드의 startDate와 마지막 레코드의 endDate 사용
    const firstRecord = reports[0];
    const lastRecord = reports[reports.length - 1];
    
    const startDate = formatDate(firstRecord.startDate || firstRecord.start_date);
    const endDate = formatDate(lastRecord.endDate || lastRecord.end_date);
    
    if (startDate && endDate) {
      return `${startDate}~${endDate}`;
    } else if (startDate) {
      return startDate;
    } else if (endDate) {
      return endDate;
    }
    
    return '날짜미상';
  }
};

export default reportService;
